import {
  Component,
  input,
  inject,
  signal,
  computed,
  OnInit,
  effect,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Icon } from "../utils/icon/icon";
import { QuestionnaireSectionInterface } from "src/app/core/models/mobility-questionnaire";
import { QuestionnaireStore } from "src/app/features/store/questionnaire-store";
import { FormsModule } from "@angular/forms";
import { TranslocoModule } from "@jsverse/transloco";

@Component({
  selector: "questionnaire-section",
  imports: [Icon, FormsModule, TranslocoModule],
  templateUrl: "./questionnaire-section.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      details .icon-closed {
        display: block;
      }

      details .icon-open {
        display: none;
      }

      details[open] .icon-closed {
        display: none;
      }

      details[open] .icon-open {
        display: block;
      }
    `,
  ],
})
export class QuestionnaireSection implements OnInit {
  title = input.required<string>();
  section = input.required<QuestionnaireSectionInterface>();
  sectionIndex = input.required<number>();

  private readonly store = inject(QuestionnaireStore);
  isSaving = signal(false);

  answers = computed(() => {
    return this.store.getSectionAnswers(this.sectionIndex());
  });

  constructor() {
    effect(() => {
      this.section();
      this.sectionIndex();
      this.answers();
      this.hydrateAnswersFromSection();
    });
  }

  ngOnInit(): void {
    this.hydrateAnswersFromSection();
  }

  compareRadioButtonAnswerWithAnswerIndex(
    index: number,
    answer: string
  ): boolean {
    if (Number.parseInt(answer) === index) {
      return true;
    }
    return false;
  }

  getAnswer(questionIndex: number): string {
    return this.answers()[questionIndex] || "";
  }

  isMultiChoiceSelected(questionIndex: number, choiceIndex: number): boolean {
    const selectedValues = this.parseMultiChoiceAnswer(
      this.getAnswer(questionIndex)
    );
    return selectedValues.includes(choiceIndex.toString());
  }

  maturitySectionTranslations = [
    "Synthèse et évaluation du projet de mobilité",
    "Mobility Summary",
    "Resumen y evaluación del proyecto de movilidad",
    "Podsumowanie i ocena projektu mobilności",
    "Shrnutí a hodnocení projektu mobility",
    "Zusammenfassung und Bewertung des Mobilitätsprojekts",
  ];

  setAnswer(questionIndex: number, value: string): void {
    if (
      this.maturitySectionTranslations.some((translation) =>
        this.title().includes(translation)
      )
    ) {
      this.store.setMaturityHasChanged(true);
    }
    const currentAnswers = this.answers();
    this.store.setSectionAnswers(this.sectionIndex(), {
      ...currentAnswers,
      [questionIndex]: value,
    });
  }

  onMultiChoiceChange(
    questionIndex: number,
    choiceIndex: number,
    checked: boolean
  ): void {
    const currentSelectedValues = this.parseMultiChoiceAnswer(
      this.getAnswer(questionIndex)
    );
    const selectedSet = new Set(currentSelectedValues);
    const choiceValue = choiceIndex.toString();

    if (checked) {
      selectedSet.add(choiceValue);
    } else {
      selectedSet.delete(choiceValue);
    }

    this.setAnswer(questionIndex, JSON.stringify(Array.from(selectedSet)));
  }

  private hydrateAnswersFromSection(): void {
    const currentAnswers = this.answers();
    const nextAnswers: { [questionIndex: number]: string } = {
      ...currentAnswers,
    };
    let hasChanges = false;

    this.section().questions.forEach((question, questionIndex) => {
      if (nextAnswers[questionIndex] !== undefined) {
        return;
      }

      if (typeof question.answer === "string" && question.answer.length > 0) {
        nextAnswers[questionIndex] = question.answer;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.store.setSectionAnswers(this.sectionIndex(), nextAnswers);
    }
  }

  private parseMultiChoiceAnswer(rawAnswer: string): string[] {
    if (!rawAnswer) {
      return [];
    }

    try {
      const parsed = JSON.parse(rawAnswer);
      return Array.isArray(parsed)
        ? parsed.filter((value): value is string => typeof value === "string")
        : [];
    } catch {
      return [];
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  async saveAnswers(): Promise<void> {
    if (this.store.maturityHasChanged()) {
      this.store.setIsModalMaturityOpen(true);
    }
    try {
      this.isSaving.set(true);
      this.store.setDirty(false);

      const questionnaire = this.store.questionnaire();
      const allSectionAnswers = this.store.sectionAnswers();

      const submissionSections = questionnaire.sections.map(
        (section, sectionIdx) => {
          const sectionAnswers = allSectionAnswers[sectionIdx] || {};

          const submissionQuestions = section.questions.map(
            (q, questionIdx) => {
              return {
                questionId: q.questionId,
                answer: sectionAnswers[questionIdx] || undefined,
              };
            }
          );

          return {
            sectionId: section.sectionId,
            questions: submissionQuestions,
          };
        }
      );

      const submission = {
        sections: submissionSections,
      };

      await this.store.saveSubmission(submission);
    } finally {
      this.isSaving.set(false);
      this.store.setMaturityHasChanged(false);
    }
  }
}
